/**
 * desktop-menu-drawer.js
 */

import React from 'react';
import { alpha, Typography, IconButton, Slide } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Switch } from 'react-router-dom';
import { useHookstate } from '@hookstate/core';
import Route from 'routing/route';
import FeatureBox from 'components/feature-box';
import Card from 'components/card';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
  },
  card: {
    boxShadow: theme.shadows[4],
  },
  subtitle: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    }
  },
  headerSaveButton: {
    "&:disabled, &.MuiButton-contained:hover.Mui-disabled": {
      backgroundColor: theme.palette.grey.colors[7],
      color: theme.palette.grey.colors[5],
    },
  },
  headerMenuButton: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    }
  },
  menuContainer: {
    backgroundColor: theme.palette.comp.menuDrawer.menuBackground,
    minWidth: `${theme.size.menuDrawer.leftPanelMinWidth}px`,
    maxWidth: `${theme.size.menuDrawer.leftPanelMaxWidth}px`,
    width: '33%',
    padding: `${theme.spacing(1)} ${theme.size.menuDrawer.menuPadding}px`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    zIndex: 20,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    }
  },
  helpContainer: {
    background: `linear-gradient(to top, ${alpha(theme.palette.comp.menuDrawer.menuBackground, 0.8)} 90%, transparent 100%)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    zIndex: 1,
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    '& > div': {
      marginTop: theme.spacing(2)
    }
  },
  helpTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.grey.colors[6]}`,
    paddingBottom: theme.spacing(0.5),
  },
  helpInfo: {
    whiteSpace: 'pre-line',
    fontSize: '0.85rem',
    color: theme.palette.sys.onSurfaceVariant,
    overflow: 'auto',
    '& div': {
      marginBottom: theme.spacing(1.5),
    },
  },
  drawerContent: {
    // overflowY: 'auto',
    // overflowX: 'hidden',
    backgroundColor: theme.palette.sys.surfaceContainer,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
}));


const DrawerSidebarText = function(props) {
  const classes = useStyles();
  const sidebarText = useHookstate(props.sidebarText);
  const isFocused = sidebarText.isFocused.get();
  const title = sidebarText.text.title.get();
  const info = sidebarText.text.info.get();
  const [text, setText] = React.useState();


  React.useEffect(() => {
    if (!isFocused) {
      setText({ title, info });
    }
  }, [isFocused, title, info]);


  return (
    <>
      {text && text.title &&
      <div className={classes.helpContainer} data-testid="help-container">
        <Slide direction="up" in={true} timeout={250} key={text.title}>
          <div style={{ overflow: 'auto' }}>
            <Typography variant="body1" className={classes.helpTitle}>
              {text.title}
            </Typography>
            <Typography variant="body2" component="div" className={classes.helpInfo}>
              {text.info}
            </Typography>
          </div>
        </Slide>
      </div>}
    </>
  );
}


export default function DesktopMenuDrawer(props) {
  const {
    routes,
    menuRoutes,
    onClose,
    title,
    subtitle,
    drawerState,
    recordGroupType,
  } = props;
  const classes = useStyles();
  // const shortHeight = useMediaQuery('(max-height:700px');
  // const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));


  // inserts additional props into provided route handlers
  const processRoutes = (routes) => {
    return routes.map(route => {
      let newRoute = {...route};
      newRoute.render = () => {
        return route.component
          ? <route.component {...props} />
          : null;
      };
      delete newRoute.component;
      return newRoute;
    });
  } 


  // drop menu action
  // const performMenuAction = (action) => {
  //   const panelId = panels.findIndex(x => x.id === action);
  //   changePanelHandler(panelId);
  // }


  return (
    <>
      <div
        data-testid='desktop-drawer'
        className={classes.root}
      >
        <Card
          titleStyle="dark"
          recordGroupType={recordGroupType}
          className={classes.card}
          title={
            <span data-testid="drawer-header-title" style={{ paddingRight: 20 }}>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography variant="h6">{title}</Typography>
                {subtitle &&
                <Typography variant="body2" className={classes.subtitle}>{subtitle}</Typography>}
              </div>
            </span>
          }
          leftHeaderActions={
            <>
              <IconButton data-testid="desktop-drawer-close-button" onClick={onClose} size="small" style={{color: 'inherit'}}>
                <Icon type='close' />
              </IconButton>
            </>
          }
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          <FeatureBox
            style={{
              flex: 1,
              display: 'flex',
              overflow: 'hidden'
            }}
            loading={drawerState.loading.get().menu || drawerState.loading.get().content}
            spinnerSize={60}
            spinnerTop={-25}
          >

            {/* menu container */}
            <div className={`${classes.menuContainer} drawer-menu`}>
              <Switch>
                {processRoutes(menuRoutes).map((route, i) => <Route key={i} {...route} />)}
              </Switch>

              {/* sidebar text */}
              <DrawerSidebarText sidebarText={drawerState.sidebarText} />
            </div>

            {/* right panel */}
            <div className={classes.drawerContent} data-testid="desktop-drawer-content-panel">
              <Switch>
                {processRoutes(routes).map((route, i) => <Route key={i} {...route} />)}
              </Switch>
            </div>
          </FeatureBox>
        </Card>
      </div>
    </>
  );
}